<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import LogoIcon from './LogoIcon.vue'

const { t, locale } = useI18n()
const isScrolled = ref(false)
const isMobileMenuOpen = ref(false)

const toggleLang = () => {
  locale.value = locale.value === 'zh-CN' ? 'en-US' : 'zh-CN'
}

const currentLangLabel = computed(() => {
  return locale.value === 'zh-CN' ? t('language.en') : t('language.zh')
})

const navLinks = [
  { key: 'product', href: '#product' },
  { key: 'solutions', href: '#solutions' },
  { key: 'terminals', href: '#terminals' }
]

const handleScroll = () => {
  isScrolled.value = window.scrollY > 40
}

onMounted(() => {
  window.addEventListener('scroll', handleScroll, { passive: true })
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
})
</script>

<template>
  <header
    :class="['navbar', { 'navbar--scrolled': isScrolled }]"
    role="banner"
  >
    <div class="navbar__inner container">
      <a href="#" class="navbar__brand" aria-label="JMTMS">
        <LogoIcon class="navbar__logo" />
        <span class="navbar__name">积米 JMTMS</span>
      </a>

      <nav class="navbar__nav" role="navigation" aria-label="Main">
        <a
          v-for="link in navLinks"
          :key="link.key"
          :href="link.href"
          class="navbar__link"
        >
          {{ t(`nav.${link.key}`) }}
        </a>
      </nav>

      <div class="navbar__actions">
        <button
          class="navbar__lang"
          @click="toggleLang"
          :aria-label="`Switch to ${currentLangLabel}`"
        >
          {{ currentLangLabel }}
        </button>
        <a href="#contact" class="btn btn-primary navbar__cta">
          {{ t('nav.contact') }}
        </a>

        <button
          class="navbar__mobile-toggle"
          @click="isMobileMenuOpen = !isMobileMenuOpen"
          :aria-expanded="isMobileMenuOpen"
          aria-label="Toggle menu"
        >
          <span />
          <span />
        </button>
      </div>
    </div>

    <div
      :class="['navbar__mobile', { 'navbar__mobile--open': isMobileMenuOpen }]"
      role="dialog"
      aria-modal="true"
    >
      <a
        v-for="link in navLinks"
        :key="link.key"
        :href="link.href"
        class="navbar__mobile-link"
        @click="isMobileMenuOpen = false"
      >
        {{ t(`nav.${link.key}`) }}
      </a>
      <hr />
      <a href="#contact" class="btn btn-primary" @click="isMobileMenuOpen = false">
        {{ t('nav.contact') }}
      </a>
    </div>
  </header>
</template>

<style scoped>
.navbar {
  position: fixed;
  inset: 0 0 auto 0;
  z-index: 1000;
  padding-block: 1rem;
  transition: background var(--duration-fast) var(--ease-out-expo),
    box-shadow var(--duration-fast) var(--ease-out-expo),
    padding var(--duration-fast) var(--ease-out-expo);
}

.navbar--scrolled {
  background: rgba(250, 249, 247, 0.82);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  box-shadow: 0 1px 0 rgba(0, 0, 0, 0.05);
  padding-block: 0.75rem;
}

.navbar__inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 2rem;
}

.navbar__brand {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  color: var(--color-dark);
  flex-shrink: 0;
}

.navbar__logo {
  width: 32px;
  height: 32px;
  color: var(--color-dark);
}

.navbar__name {
  font-family: var(--font-display);
  font-size: 1.25rem;
  font-weight: 700;
  letter-spacing: -0.02em;
}

.navbar__nav {
  display: flex;
  align-items: center;
  gap: 2.5rem;
}

.navbar__link {
  position: relative;
  font-size: 0.9375rem;
  font-weight: 500;
  color: var(--color-text-secondary);
  transition: color var(--duration-fast) var(--ease-out-expo);
}

.navbar__link::after {
  content: '';
  position: absolute;
  left: 0;
  bottom: -4px;
  width: 0;
  height: 1.5px;
  background: var(--color-accent);
  transition: width var(--duration-fast) var(--ease-out-expo);
}

.navbar__link:hover {
  color: var(--color-text);
}

.navbar__link:hover::after {
  width: 100%;
}

.navbar__actions {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.navbar__lang {
  padding: 0.5rem 0.875rem;
  border-radius: 999px;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--color-text-secondary);
  transition: background var(--duration-fast) var(--ease-out-expo),
    color var(--duration-fast) var(--ease-out-expo);
}

.navbar__lang:hover {
  background: var(--color-surface);
  color: var(--color-text);
}

.navbar__cta {
  padding: 0.625rem 1.25rem;
  font-size: 0.875rem;
}

.navbar__mobile-toggle {
  display: none;
  flex-direction: column;
  justify-content: center;
  gap: 5px;
  width: 36px;
  height: 36px;
  padding: 0.5rem;
}

.navbar__mobile-toggle span {
  display: block;
  width: 100%;
  height: 2px;
  background: var(--color-dark);
  border-radius: 2px;
  transition: transform var(--duration-fast) var(--ease-out-expo);
}

.navbar__mobile {
  position: fixed;
  inset: 64px 0 auto 0;
  background: rgba(250, 249, 247, 0.96);
  backdrop-filter: blur(20px);
  padding: 1.5rem 2rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  transform: translateY(-120%);
  opacity: 0;
  transition: transform var(--duration-fast) var(--ease-out-expo),
    opacity var(--duration-fast) var(--ease-out-expo);
  pointer-events: none;
}

.navbar__mobile--open {
  transform: translateY(0);
  opacity: 1;
  pointer-events: auto;
}

.navbar__mobile-link {
  font-size: 1.25rem;
  font-weight: 600;
  padding-block: 0.5rem;
}

.navbar__mobile hr {
  border: none;
  border-top: 1px solid var(--color-border);
  margin-block: 0.5rem;
}

@media (max-width: 900px) {
  .navbar__nav,
  .navbar__cta {
    display: none;
  }

  .navbar__mobile-toggle {
    display: flex;
  }
}
</style>
